/**
 * The typed half of Ivy's token set (design spec §3).
 *
 * `styles/tokens.css` is the runtime source of truth; this module exists for
 * the handful of decisions TypeScript has to make — which accent an avatar
 * gets, which strength colour a password scores, how a countdown formats. Every
 * value here is a *reference to a CSS variable*, never a literal colour, so
 * there is still exactly one place a hex code lives.
 */
export const SURFACE_TOKENS = ["canvas", "surface", "raised", "muted"];
export const ACCENT_TOKENS = [
    "brand",
    "play",
    "coin",
    "good",
    "warn",
    "ember",
    "lilac",
    "sand",
];
/** `cssVar("coin")` → `var(--wr-coin)`. */
export function cssVar(token) {
    return `var(--wr-${token})`;
}
/**
 * **LOCKED (§3.1): the same player is the same colour everywhere, forever.**
 *
 * Avatar and seat colours rotate through this list by a stable hash of
 * `players.id`. It must stay a pure function of the id — no shuffling, no
 * "nicer distribution", no per-screen variation. A player who learns their
 * colour in the lobby must still recognise it in a room.
 */
export const IDENTITY_COLOURS = [
    "warn",
    "good",
    "coin",
    "play",
    "lilac",
    "sand",
];
/** FNV-1a. Small, stable across platforms, and not a security boundary. */
export function stableHash(value) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < value.length; i += 1) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash >>> 0;
}
export function identityColour(id) {
    const colours = IDENTITY_COLOURS;
    return colours[stableHash(id) % colours.length];
}
/** Initials fallback for an avatar with no art (§16 `wr-avatar-set`). */
export function initialsFor(name) {
    const cleaned = name.replace(/[^A-Za-z0-9]+/g, " ").trim();
    if (cleaned.length === 0)
        return "??";
    const parts = cleaned.split(/\s+/);
    if (parts.length >= 2) {
        return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
    }
    return cleaned.slice(0, 2).toUpperCase();
}
/**
 * `m:ss` for every countdown in the product (§6 lockout, §7 resend).
 * Clamped at zero: a negative timer is a rendering bug the player should never
 * be shown.
 */
export function formatCountdown(seconds) {
    const total = Math.max(0, Math.ceil(seconds));
    const minutes = Math.floor(total / 60);
    const rest = total % 60;
    return `${minutes}:${rest.toString().padStart(2, "0")}`;
}
/**
 * Password strength, 0–4, matching the `StrengthMeter`'s four cells (§6).
 *
 * Deliberately a transparent rule set rather than an entropy estimator: the
 * requirement list under the meter must be able to say *which* rule is unmet,
 * and a score a player cannot reverse-engineer is a score they cannot act on.
 * The only enforced rule is length — the server's `minPasswordLength` — and the
 * rest raise the score without gating submission.
 */
export const PASSWORD_RULE_IDS = ["length", "case", "number"];
export function scorePassword(password, minLength) {
    const met = {
        length: password.length >= minLength,
        case: /[a-z]/.test(password) && /[A-Z]/.test(password),
        number: /[0-9]/.test(password) || /[^A-Za-z0-9]/.test(password),
    };
    let score = 0;
    if (met.length)
        score += 1;
    if (met.case)
        score += 1;
    if (met.number)
        score += 1;
    if (password.length >= minLength + 4 && met.length)
        score += 1;
    return { score: Math.min(4, score), met, acceptable: met.length };
}
/** Which fill the meter's filled cells take (§6). */
export function strengthTone(score) {
    if (score >= 4)
        return "strong";
    if (score === 3)
        return "fair";
    return "weak";
}
//# sourceMappingURL=tokens.js.map